import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Sparkles, Calendar, Database, ArrowRight, CheckCircle2, Lock } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

const ICON_MAP = {
  Sparkles,
  Calendar,
  Database,
};

const AppsWidget = () => {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    fetchAppsData();

    const handleVisible = () => {
      if (document.visibilityState === 'visible') fetchAppsData();
    };
    document.addEventListener('visibilitychange', handleVisible);
    return () => document.removeEventListener('visibilitychange', handleVisible);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchAppsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const userId = user.id;

      const [appsResult, subsResult] = await Promise.all([
        supabase.from('apps').select('*').order('sort_order'),
        supabase
          .from('user_subscriptions')
          .select('app_id')
          .eq('user_id', userId)
          .eq('is_active', true),
      ]);

      if (appsResult.error) throw appsResult.error;
      if (subsResult.error) throw subsResult.error;

      const activeSet = new Set((subsResult.data || []).map((s) => s.app_id));

      setApps(
        (appsResult.data || []).map((app) => ({
          ...app,
          isActive: activeSet.has(app.id),
          icon: ICON_MAP[app.icon_name] ?? Sparkles,
        }))
      );
    } catch (err) {
      console.error('Error cargando apps:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="flex flex-col h-full bg-white/75 backdrop-blur-md border border-white/90 shadow-[0_4px_20px_rgba(99,102,241,0.06)] dark:bg-[#111114] dark:border-[#1e1e24] dark:shadow-none hover:shadow-[0_8px_30px_rgba(99,102,241,0.1)] dark:hover:shadow-none transition-all duration-300 overflow-hidden rounded-2xl">
      <div className="flex items-center justify-between p-5 pb-4 border-b border-indigo-50/50 dark:border-[#1a1a20]">
        <div className="flex items-center gap-3">
          <h3 className="text-[#1a1625] dark:text-[#e5e5e5] font-semibold">Ventes Ecosystem</h3>
        </div>
        <button className="text-xs text-indigo-300 hover:text-indigo-500 dark:text-[#555] dark:hover:text-violet-400 transition-colors flex items-center gap-1">
          App Store <ArrowRight size={14} />
        </button>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-2 overflow-y-auto custom-scrollbar">
        {loading && (
          <div className="flex-1 flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-200 dark:border-white/30" />
          </div>
        )}

        {!loading && error && (
          <p className="text-xs text-red-400 text-center mt-4">{error}</p>
        )}

        {!loading && !error && apps.length === 0 && (
          <p className="text-xs text-[#9ca3af] dark:text-[#555] text-center mt-4">Sin apps disponibles</p>
        )}

        {!loading && !error && apps.map((app) => {
          const isClickable = app.isActive && !!app.url;
          const Wrapper = isClickable ? 'a' : 'div';
          const wrapperProps = isClickable
            ? { href: app.url, target: '_blank', rel: 'noopener noreferrer' }
            : {};

          return (
            <Wrapper
              key={app.id}
              {...wrapperProps}
              className={`flex items-center justify-between p-3 rounded-2xl border transition-all ${
                app.isActive
                  ? 'bg-indigo-50/30 dark:bg-black/20 border-indigo-100 dark:border-white/10 cursor-pointer hover:bg-indigo-50/60 hover:border-indigo-200 dark:hover:bg-white/5 dark:hover:border-white/20'
                  : 'border-transparent opacity-60 grayscale-[50%] cursor-default'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${app.bg_color} ${app.color}`}>
                  <app.icon size={20} />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-[#1a1625] dark:text-[#e5e5e5]">{app.name}</h4>
                  <p className="text-[10px] text-[#9ca3af] dark:text-[#555]">{app.description}</p>
                </div>
              </div>

              <div>
                {app.isActive ? (
                  <CheckCircle2 size={18} className="text-emerald-500" />
                ) : (
                  <div className="flex items-center gap-1 bg-indigo-50 dark:bg-white/5 px-2 py-1 rounded-full">
                    <Lock size={12} className="text-indigo-300 dark:text-[#555]" />
                    <span className="text-[10px] text-indigo-400 dark:text-[#555] font-medium">Upgrade</span>
                  </div>
                )}
              </div>
            </Wrapper>
          );
        })}
      </div>
    </Card>
  );
};

export default AppsWidget;
